import React, { useState } from "react";
import { MatchResponseDto } from "@/lib/api/interfaces/matches.interface";
import { useMatchesFindQuery } from "@/lib/api/requests/matches.requests";
import { formatFriendlyDate } from "@/lib/utils/date-formatter";
import { Button } from "@/ui-components/ui/button";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { Megaphone, Calendar } from "lucide-react";
import { DefaultTemplateType } from "@/page-components/post-generator/template-editor/registry/defaultTemplates";
import { MissingTemplateDialog } from "../../../util-components/dialog/MissingTemplateDialog";
import MatchDetailsDialog from "@/page-components/matches/details-dialog";
import { useDefaultTemplate } from "@/lib/hooks/use-default-template";

const UpcomingGames = () => {
    const { data: matches, isLoading } = useMatchesFindQuery({ limit: 4, filterType: "upcoming" });

    if (isLoading || !matches) {
        return <UpcomingGamesSkeleton />
    }

  return (
    <div className="divide-y divide-gray-100">
        {matches?.matches.docs.map((match) => (
            <UpcomingGame 
                key={match._id} 
                match={match} 
            />
        ))}
        {(!matches?.matches.docs || matches.matches.docs.length === 0) && (
          <div className="p-8 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 mb-3 text-gray-400">
                 <Calendar className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-gray-900">Keine bevorstehenden Spiele</p>
            <p className="text-xs text-gray-500 mt-1">Hier erscheinen deine nächsten Ansetzungen.</p>
          </div>
        )}
    </div>
  );
};

const UpcomingGame = ({ match }: { match: MatchResponseDto }) => {
  const teamName = match.team?.name || "?";
  const teamThumbnail = match.team?.thumbnailUrl || null;
  const teamLogoUrl = match.team?.logoUrl || null;

  const opponentName = match.opponent?.name || "Unbekannter Gegner";
  const opponentThumbnail = match.opponent?.thumbnailUrl || null;
  const opponentLogoUrl = match.opponent?.logoUrl || null;

  const { handleCreatePost, isLoading: isLoadingDefaults, missingTemplateDialogProps } = useDefaultTemplate({
    prefillData: {
      entityType: 'Match',
      entityId: match._id
    }
  });

  const [detailsOpen, setDetailsOpen] = useState(false);

  const matchType = match.homeOrAway === 'AWAY' ? 'Auswärtsspiel' : 'Heimspiel';

  const isAway = match.homeOrAway === 'AWAY';
  const homeThumbnail = isAway ? opponentThumbnail : teamThumbnail;
  const homeLogoUrl = isAway ? opponentLogoUrl : teamLogoUrl;
  const awayThumbnail = isAway ? teamThumbnail : opponentThumbnail;
  const awayLogoUrl = isAway ? teamLogoUrl : opponentLogoUrl;

  const handleClick = (e: React.MouseEvent) => {
    handleCreatePost(DefaultTemplateType.MATCH_ANNOUNCEMENT, "Spiel-Ankündigungen", e);
  };
  
  return (
    <>
        <div 
            onClick={() => setDetailsOpen(true)}
            className="group relative flex flex-col sm:flex-row sm:items-center justify-between p-4 hover:bg-gray-50 transition-colors gap-4 cursor-pointer"
        >
            <div className="flex items-center gap-4 min-w-0">
                <div className="flex items-center shrink-0">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center overflow-hidden z-10">
                        {homeThumbnail || homeLogoUrl ? (
                            <SmartLogoImage thumbnailUrl={homeThumbnail} fallbackUrl={homeLogoUrl} alt="Home Logo" />
                        ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-xs">?</div>
                        )}
                    </div>
                    <div className="h-10 w-10 shrink-0 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center overflow-hidden -ml-3 z-0">
                        {awayThumbnail || awayLogoUrl ? (
                            <SmartLogoImage thumbnailUrl={awayThumbnail} fallbackUrl={awayLogoUrl} alt="Away Logo" />
                        ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-xs">?</div>
                        )}
                    </div>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-gray-900 truncate">
                            {isAway ? `${opponentName} vs. ${teamName}` : `${teamName} vs. ${opponentName}`}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{formatFriendlyDate(match.date)}</span>
                        <span>•</span>
                        <span>{matchType}</span>
                        {match.location && (
                            <>
                                <span>•</span>
                                <span className="truncate max-w-[150px]">{match.location}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity sm:absolute sm:right-0 sm:top-0 sm:bottom-0 sm:pr-4 sm:pl-24 sm:bg-gradient-to-r sm:from-transparent sm:via-gray-50 sm:to-gray-50">
                <Button
                    size="sm"
                    variant="outline"
                    disabled={isLoadingDefaults}
                    onClick={handleClick}
                    className="relative z-10 h-10 text-xs bg-white px-4 rounded-xl shadow-sm border-gray-200 hover:bg-gray-50 transition-all"
                >
                    <Megaphone className="w-3.5 h-3.5 mr-1.5" />
                    Ankündigen
                </Button>
            </div>
        </div>
        <MissingTemplateDialog
            {...missingTemplateDialogProps}
        />
        <MatchDetailsDialog 
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            matchId={match._id}
        />
    </>
  );
};

const SmartLogoImage = ({
  thumbnailUrl,
  fallbackUrl,
  alt,
}: {
  thumbnailUrl?: string | null;
  fallbackUrl?: string | null;
  alt: string;
}) => {
  const src = thumbnailUrl || fallbackUrl || "";

  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-contain p-1.5"
    />
  );
};

const UpcomingGamesSkeleton = () => {
    return (
        <div className="divide-y divide-gray-100">
            {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                    </div>
                    <div className="flex gap-2">
                        <Skeleton className="h-8 w-24" />
                    </div>
                </div>
            ))}
        </div>
    )
}

export default UpcomingGames;
